import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Rechnung } from '../types';
import rechnungService from '../services/rechnungService';
import { Card, Spinner, Icon, EmptyState, Button } from './ui';
import SchnellrechnungModal from './SchnellrechnungModal';
import RechnungsZeitachse from './RechnungsZeitachse';
import {
  useVorgemerkteRechnungen, vorschauGemerkt, VorschauSchalter, VorschauKopf, Zeitraum, ZeitraumAuswahl,
} from './VorgemerkteRechnungen';
import { formatBetrag } from '../utils/preis';

/** Die Rechnungen eines Kunden, neueste zuerst – eingebettet auf der Kunden-Detailseite. */
const RechnungSektion: React.FC<{ kundeId: number }> = ({ kundeId }) => {
  const [rechnungen, setRechnungen] = useState<Rechnung[]>([]);
  const [loading, setLoading] = useState(true);
  const [schnell, setSchnell] = useState(false);
  const [vorschau, setVorschau] = useState(vorschauGemerkt);
  const [zeitraum, setZeitraum] = useState<Zeitraum>('monat');
  const vorgemerkt = useVorgemerkteRechnungen(vorschau, zeitraum, kundeId);

  const load = useCallback(async () => {
    try { setRechnungen(await rechnungService.getAll({ kundeId })); }
    catch { toast.error('Rechnungen konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, [kundeId]);

  useEffect(() => { load(); }, [load]);

  const offen = rechnungen
    .filter(r => r.status === 'GESTELLT')
    .reduce((s, r) => s + r.summeBrutto, 0);

  return (
    <Card className="p-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900">Rechnungen</h2>
        <div className="flex flex-wrap items-center gap-3">
          {offen > 0 && (
            <span className="text-sm text-gray-500">{formatBetrag(offen)} offen</span>
          )}
          <VorschauSchalter aktiv={vorschau} onAktiv={setVorschau} />
          <Button size="sm" variant="secondary" onClick={() => setSchnell(true)} icon={<Icon.Plus className="h-4 w-4" />}>
            Schnellrechnung
          </Button>
        </div>
      </div>

      {schnell && <SchnellrechnungModal kundeId={kundeId} onClose={() => setSchnell(false)} />}

      {/* Zeitraum unter der Kopfzeile statt darin – sonst wird die auf dem iPad zu voll. */}
      {vorschau && (
        <div className="mb-1 flex justify-end">
          <ZeitraumAuswahl zeitraum={zeitraum} onZeitraum={setZeitraum} />
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-gray-500"><Spinner /> Lädt…</div>
      ) : rechnungen.length === 0 && vorgemerkt.rechnungen.length === 0 ? (
        <>
          {vorschau && <VorschauKopf daten={vorgemerkt} />}
          <EmptyState
            icon={<Icon.Doc />}
            title="Noch keine Rechnungen"
            hint="Zu jedem Jahrestag der Vertragsunterschrift entsteht automatisch ein Entwurf für das kommende Vertragsjahr."
            action={<Button variant="secondary" onClick={() => setSchnell(true)} icon={<Icon.Plus className="h-4 w-4" />}>Schnellrechnung</Button>}
          />
        </>
      ) : (
        <RechnungsZeitachse
            rechnungen={rechnungen}
            vorgemerkte={vorgemerkt.rechnungen}
            mitHeute={vorschau}
            ohneKunde
            eingebettet
            kopf={vorschau ? <VorschauKopf daten={vorgemerkt} /> : undefined}
          />
      )}
    </Card>
  );
};

export default RechnungSektion;
